import React, { useEffect, useMemo, useState } from 'react';
import { Loader } from 'components';
import { PublicationOptionFormView } from 'components/PublicationOption/PublicationOptionFormView';
import { useApp, useUpdateDeposit } from 'providers';
import { PublicationOption, Strength, User } from 'types';
import { ClassifiedsService, OffersService, Tracking } from 'api';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { InvoicesService } from 'api/Invoices/InvoicesService';
import { ROUTES } from 'routes';
import { config } from 'config';

export const RenewDepositForm: React.FC = () => {
  const { user } = useApp();
  const { classified, userData, renewClassified } = useUpdateDeposit();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [publicationOptions, setPublicationOptions] = useState<PublicationOption[]>([]);
  const [strengths, setStrengths] = useState<Strength[]>([]);

  useEffect(() => {
    ClassifiedsService.getStrengths(classified!)
      .then((r: Strength[]) => {
        setStrengths(r);
        return r.length;
      })
      .catch(() => 0)
      .then(nbStrengths => OffersService.getOffers(classified!.price!, nbStrengths).then(setPublicationOptions))
      .catch(r => {
        setErrorMessage(r.message);
        Tracking.sendCurrentPageError();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [classified]);

  const initialProducts = useMemo(() => {
    const ids: { [k: string]: string | undefined } = {
      priorisation: undefined,
      strengths: undefined,
      pack: undefined,
      callTracking: undefined,
    };

    classified!.publicationOptions!.forEach(p => {
      if (p === 'STRENGTHS') {
        ids.strengths = publicationOptions.find(po => po.name === 'points_forts')?.id;
      }

      if (p === 'PRIORISATION') {
        ids.priorisation = publicationOptions.find(po => po.name === 'priorisation')?.id;
      }
    });
    const packId = publicationOptions.find(po => po.name === 'pack')?.id;

    const securitelOption = publicationOptions.find(po => po.name === 'securitel')!;

    if (securitelOption) {
      if (classified?.publicationOptions!.includes('CALLTRACKING')) {
        ids.callTracking = 'PO_SECURITEL';
        securitelOption.checked = true;
      } else {
        securitelOption.checked = false;
      }
    }

    if (packId && ids.strengths && ids.priorisation) {
      ids.pack = packId;
    }

    const selectIds = [ids.pack, ids.priorisation, ids.strengths, ids.callTracking].filter(
      p => p !== undefined,
    ) as string[];

    selectIds.forEach(id => {
      const option = publicationOptions.find(o => o.id === id)!;
      if (ids.pack) {
        if (option.id === ids.strengths || option.id === ids.priorisation) {
          option.active = false;
          option.checked = true;
        } else {
          option.active = true;
          option.checked = true;
        }
      } else {
        option.active = true;
        option.checked = true;
      }
    });

    return selectIds as string[];
  }, [classified, publicationOptions]);

  const { values, isSubmitting, errors, setFieldValue, handleChange, handleSubmit } = useFormik({
    initialValues: {
      productIds: initialProducts,
      cgu: false,
      totalPrice: 0,
      pbxTypeCarte: undefined,
    },
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      if (!values.cgu) {
        setFieldError('cgu', 'Veuillez acceptez les informations légales.');
        setSubmitting(false);
        Tracking.sendCurrentPageError();
        return;
      }

      if (values.totalPrice && !values.pbxTypeCarte) {
        setFieldError('pbxTypeCarte', 'Veuillez choisir une option de paiement.');
        setSubmitting(false);
        Tracking.sendCurrentPageError();
        return;
      }
      try {
        await renewClassified(values.productIds.includes('PO_SECURITEL'), values.totalPrice === 0);
        if (values.totalPrice && values.pbxTypeCarte) {
          const invoice = await InvoicesService.createTransaction(
            classified!.reference,
            userData as User,
            values.productIds,
          );
          if (invoice) {
            history.push(ROUTES.payment, {
              transactionId: invoice.id,
              username: invoice.username,
              price: invoice.price,
              typeCarte: values.pbxTypeCarte,
              vehicleCategory: classified!.vehicle.category,
              redirectionOnError: ROUTES.renew(classified!.reference, 'renouvellement'),
              action: 'renew',
            });
          }
        } else {
          setTimeout(() => {
            if (user!.isPro) {
              window.location.href = config.URL_PILOT + 'annonces';
            } else {
              window.location.href = config.URL_LACENTRALE + 'compte/annonces';
            }
          }, 2500);
        }
      } catch (e) {
        const message = (e as Error)?.message || '';
        setErrorMessage(message);
        Tracking.sendCurrentPageError();
        setSubmitting(false);
      }
    },
  });

  return loading ? (
    <Loader />
  ) : (
    <PublicationOptionFormView
      publicationOptions={publicationOptions}
      strengths={strengths}
      values={values}
      isSubmitting={isSubmitting}
      errors={errors}
      setFieldValue={setFieldValue}
      onChange={handleChange}
      onReturn={history.goBack}
      onSubmit={handleSubmit}
      phone={classified!.contacts!.phone1.value}
      errorMessage={errorMessage}
      isRenewal
    />
  );
};
